import { findIndexesOf_Buffer } from '../stream/findIndexesOf';
import type { ReadonlyBuffer } from '../types';
import type { RandomAccessFileReader } from './types';

type OffsetOfTokenWithContext_Buffer = {
	offset: number;
	context: Buffer;
	before: Buffer;
	after: Buffer;
	startOffset: number;
	endOffsetPlusOne: number;
};

export type OffsetOfTokenWithContext_String = {
	offset: number;
	context: string;
	before: string;
	after: string;
	startOffset: number;
	endOffsetPlusOne: number;
};

type FindOffsetsOfTokenWithContextOptions = {
	fileReader: RandomAccessFileReader;
	needle: ReadonlyBuffer | string;
	bytesBefore: number;
	bytesAfter: number;
	encoding?: BufferEncoding; // Defaults to Buffer

	// RandomAccessFileReader#streamBytes() options
	concurrency?: number; // Defaults to 2
	highWaterMark?: number; // Defaults to 1 MiB
	chunkSize?: number; // Defaults to 256 KiB

	// findIndexesOf_Buffer options
	allowOverlap?: boolean; // Defaults to false
	forceResume?: boolean; // Defaults to true
	forcePause?: boolean; // Defaults to false
};

type FindOffsetsOfTokenWithContextOptions_Buffer = FindOffsetsOfTokenWithContextOptions & {
	needle: ReadonlyBuffer;
	encoding?: undefined; // Forced undefined, indiating Buffer results
};

type FindOffsetsOfTokenWithContextOptions_String = FindOffsetsOfTokenWithContextOptions & {
	needle: string; // Will use "encoding" to convert to Buffer
	encoding: BufferEncoding; // Forced string, indiating Buffer results
};

// Buffer overload
export async function findOffsetsOfTokenWithContext(
	options: FindOffsetsOfTokenWithContextOptions_Buffer
): Promise<OffsetOfTokenWithContext_Buffer[]>;
// String overload
export async function findOffsetsOfTokenWithContext(
	options: FindOffsetsOfTokenWithContextOptions_String
): Promise<OffsetOfTokenWithContext_String[]>;
export async function findOffsetsOfTokenWithContext({
	fileReader,
	needle,
	bytesBefore,
	bytesAfter,
	encoding,
	concurrency,
	highWaterMark,
	chunkSize,
	allowOverlap,
	forceResume,
	forcePause,
}: FindOffsetsOfTokenWithContextOptions): Promise<
	(OffsetOfTokenWithContext_String | OffsetOfTokenWithContext_Buffer)[]
> {
	const fileSize = fileReader.size;
	const wholeFileStream = await fileReader.streamBytes(0, fileSize, {
		concurrency: concurrency,
		highWaterMark: highWaterMark,
		chunkSize: chunkSize,
	});

	let needle2: ReadonlyBuffer;

	if (typeof needle === 'string') {
		// We have been given a string needle, which means we should have an encoding option
		needle2 = Buffer.from(needle, encoding ?? 'binary');
	} else {
		needle2 = needle;
	}

	const offsets = await findIndexesOf_Buffer(wholeFileStream, needle2, {
		allowOverlap: allowOverlap,
		forceResume: forceResume,
		forcePause: forcePause,
	});

	const needleLen = needle2.length;

	const results: (OffsetOfTokenWithContext_String | OffsetOfTokenWithContext_Buffer)[] = [];

	for (const offset of offsets) {
		const bytesToReadBefore = Math.min(offset, bytesBefore);
		const bytesToReadAfter = Math.min(fileSize - offset - needleLen, bytesAfter);
		const buf = await fileReader.readBytes(
			offset - bytesToReadBefore,
			bytesToReadBefore + needleLen + bytesToReadAfter
		);

		if (encoding) {
			results.push({
				offset,
				context: buf.toString(encoding),
				before: buf.toString(encoding, 0, bytesToReadBefore),
				after: buf.toString(encoding, buf.length - bytesToReadAfter),
				startOffset: offset - bytesToReadBefore,
				endOffsetPlusOne: offset + needleLen + bytesToReadAfter,
			});
		} else {
			results.push({
				offset,
				context: buf,
				before: buf.slice(0, bytesToReadBefore),
				after: buf.slice(buf.length - bytesToReadAfter),
				startOffset: offset - bytesToReadBefore,
				endOffsetPlusOne: offset + needleLen + bytesToReadAfter,
			});
		}
	}

	return results;
}
